import React, { useCallback, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronDown, ChevronUp, Plus, Rocket, Trash2 } from 'lucide-react';
import { useAdminAuth } from '../AdminAuthContext';
import {
  MAX_HOMEPAGE_SLIDER_ITEMS,
  addSliderMovie,
  fetchHomepagePublishSettings,
  fetchMoviesAdmin,
  fetchSliderItemsAdmin,
  moveSliderMovie,
  publishHomepageSlider,
  removeSliderMovie
} from '../adminApi';

export default function SliderPage() {
  const { canEditMovies } = useAdminAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishSettings, setPublishSettings] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const loadPublish = useCallback(async () => {
    const { settings } = await fetchHomepagePublishSettings();
    setPublishSettings(settings);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const res = await fetchSliderItemsAdmin();
    if (res.error) {
      setError(res.error.message || 'Failed to load slider');
      setRows([]);
    } else {
      setRows(res.rows || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadPublish();
  }, [loadPublish]);

  const confirmPublishSlider = async () => {
    if (!canEditMovies) return;
    setPublishing(true);
    const { error } = await publishHomepageSlider();
    setPublishing(false);
    if (error) {
      alert(error.message || 'Publish failed');
      return;
    }
    await loadPublish();
    setPublishDialogOpen(false);
  };

  useEffect(() => {
    let cancelled = false;
    const q = search.trim();
    if (q.length < 2) {
      setSearchResults([]);
      return undefined;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      const res = await fetchMoviesAdmin({
        page: 1,
        pageSize: 15,
        search: q,
        channel: '',
        sortField: 'published_date',
        sortAsc: false
      });
      if (!cancelled) {
        setSearching(false);
        if (res.error) setSearchResults([]);
        else setSearchResults(res.rows || []);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [search]);

  const sliderVideoIds = new Set(rows.map((r) => r.video_id));

  const handleAdd = async (videoId) => {
    if (!canEditMovies) return;
    if (rows.length >= MAX_HOMEPAGE_SLIDER_ITEMS) {
      alert(
        `You can add at most ${MAX_HOMEPAGE_SLIDER_ITEMS} movies to the homepage slider.`
      );
      return;
    }
    if (sliderVideoIds.has(videoId)) {
      alert('This movie is already on the slider.');
      return;
    }
    setBusyId(videoId);
    const { error: err } = await addSliderMovie(videoId);
    setBusyId(null);
    if (err) {
      alert(err.message || 'Could not add to slider');
      return;
    }
    setSearch('');
    setSearchResults([]);
    load();
  };

  const handleRemove = async (videoId) => {
    if (!canEditMovies) return;
    if (!window.confirm('Remove this movie from the homepage slider?')) return;
    setBusyId(videoId);
    const { error: err } = await removeSliderMovie(videoId);
    setBusyId(null);
    if (err) {
      alert(err.message || 'Remove failed');
      return;
    }
    load();
  };

  const handleMove = async (videoId, direction) => {
    if (!canEditMovies) return;
    setBusyId(videoId);
    const { error: err } = await moveSliderMovie(videoId, direction);
    setBusyId(null);
    if (err) {
      alert(err.message || 'Reorder failed');
      return;
    }
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white">Homepage slider</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Build the hero lineup below, then use{' '}
            <strong className="text-zinc-400 font-medium">Publish hero</strong> so
            visitors see it (overrides random hero when published). Lower # =
            earlier in the carousel. Max {MAX_HOMEPAGE_SLIDER_ITEMS} movies.
          </p>
        </div>
        {canEditMovies && (
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <span
              className={`text-xs font-medium px-2.5 py-1 rounded-md border ${
                publishSettings?.slider_published
                  ? 'border-emerald-800 bg-emerald-950/50 text-emerald-300'
                  : 'border-zinc-700 bg-zinc-900 text-zinc-500'
              }`}
            >
              {publishSettings?.slider_published
                ? 'Hero live on homepage'
                : 'Hero not published'}
            </span>
            <button
              type="button"
              onClick={() => setPublishDialogOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-amber-800/60 bg-amber-950/40 px-3 py-2 text-sm font-medium text-amber-200 hover:bg-amber-950/70"
            >
              <Rocket className="h-4 w-4" />
              Publish hero
            </button>
          </div>
        )}
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}

      {canEditMovies && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-2">
          <div className="text-sm font-medium text-zinc-300">Add movie</div>
          <p className="text-xs text-zinc-500">
            Type at least 2 characters to search the catalog by title,
            description, or channel.
          </p>
          <input
            type="search"
            placeholder="Search movies…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-white"
          />
          {searching && (
            <div className="text-xs text-zinc-500">Searching…</div>
          )}
          {search.trim().length >= 2 && !searching && searchResults.length > 0 && (
            <ul className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-zinc-800 divide-y divide-zinc-800">
              {searchResults.map((m) => (
                <li
                  key={m.video_id}
                  className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="text-white truncate">{m.title}</div>
                    <div className="text-xs text-zinc-500 font-mono truncate">
                      {m.video_id}
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={
                      !canEditMovies ||
                      busyId === m.video_id ||
                      sliderVideoIds.has(m.video_id) ||
                      rows.length >= MAX_HOMEPAGE_SLIDER_ITEMS
                    }
                    onClick={() => handleAdd(m.video_id)}
                    className="shrink-0 inline-flex items-center gap-1 rounded-lg bg-violet-600/80 px-2 py-1 text-xs text-white hover:bg-violet-500 disabled:opacity-40"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Dialog.Root open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/70 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(100vw-2rem,440px)] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-zinc-700 bg-zinc-900 p-6 shadow-xl focus:outline-none">
            <Dialog.Title className="text-lg font-semibold text-white mb-2">
              Publish hero to homepage?
            </Dialog.Title>
            <p className="text-sm text-zinc-400 mb-6">
              Visitors will see this ordered lineup in the homepage hero (instead of
              the automatic rotation). Publish again after you change order or
              titles.
            </p>
            <div className="flex justify-end gap-2">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="rounded-lg px-3 py-2 text-sm text-zinc-400 hover:bg-zinc-800"
                  disabled={publishing}
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                disabled={publishing || !canEditMovies}
                onClick={confirmPublishSlider}
                className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-500 disabled:opacity-50"
              >
                {publishing ? 'Publishing…' : 'Yes, publish'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <div className="rounded-xl border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-zinc-900/80 text-zinc-400 text-xs uppercase">
            <tr>
              <th className="px-3 py-2 w-16">#</th>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Video ID</th>
              {canEditMovies && <th className="px-3 py-2 w-40" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? (
              <tr>
                <td
                  colSpan={canEditMovies ? 4 : 3}
                  className="px-3 py-8 text-center text-zinc-500"
                >
                  Loading…
                </td>
              </tr>
            ) : (
              rows.map((r, i) => {
                const title = r.movie?.title || '—';
                return (
                  <tr key={r.video_id} className="hover:bg-zinc-900/50">
                    <td className="px-3 py-2 tabular-nums text-zinc-400">
                      {i + 1}
                    </td>
                    <td className="px-3 py-2 text-white max-w-xs truncate">
                      {title}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-500">
                      {r.video_id}
                    </td>
                    {canEditMovies && (
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          <button
                            type="button"
                            disabled={busyId === r.video_id || i === 0}
                            onClick={() => handleMove(r.video_id, 'up')}
                            className="p-1.5 rounded border border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-30"
                            title="Move up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            disabled={
                              busyId === r.video_id || i === rows.length - 1
                            }
                            onClick={() => handleMove(r.video_id, 'down')}
                            className="p-1.5 rounded border border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-30"
                            title="Move down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            disabled={busyId === r.video_id}
                            onClick={() => handleRemove(r.video_id)}
                            className="p-1.5 rounded text-zinc-400 hover:text-red-300 hover:bg-zinc-800"
                            title="Remove from slider"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={canEditMovies ? 4 : 3}
                  className="px-3 py-8 text-center text-zinc-500"
                >
                  No slider items — the public site will use the default hero
                  rotation until you add movies here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
